import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { createTenantStorage } from './tenantStorage';
import type { AiApplicationKind } from '@terra/shared-contracts-ai-applications';
import type { DiscoveryContentTypeConfig } from '@terra/shared-asset-processing';
import type {
  BrowserMatchReplaceRuleWithRelations,
  DomainConfig,
  HttpInterceptionRuleWithRelations,
  SignalCategoryType,
  TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import { useLayoutStore } from './layoutStore';
import { useProxyStore } from './proxy-store';
import { stopTrackingSession } from '../utils/manualSession';
import { mergeTrackingBrowserIdsAfterElectronSync } from '../utils/batchParametrizationBrowserFlush';
import {
  migrateLegacyProjectPersistedState,
  TARGET_TYPE,
  isAppTarget,
} from '../utils/target-scope.utils';

export type PortalTarget = {
  id: string;
  targetType: TargetTypeType;
  tenantId: string | null;
  name: string;
  url: string;
  redirectToUrl?: string | null;
  documentationUrls?: string[];
  environment: string;
  owner: string | null;
  isInternalApp: boolean | null;
  isBackendOnly: boolean | null;
  isDraft?: boolean | null;
  smokeTestEnabled: boolean | null;
  crawlerPeriodic?: {
    enabled: boolean;
    windowEnabled: boolean;
    dayOfWeek: number | null;
    startHour: number | null;
    timezone: string | null;
  } | null;
  automationSchedule?: {
    daysOfWeek: number[];
    startHour: number;
    endHour: number;
    timezone?: string | null;
  } | null;
  supportsParallelExecution?: boolean | null;
  signalCategoriesInScope?: SignalCategoryType[];
  discoveryContentTypes?: DiscoveryContentTypeConfig[] | null;
  domains?: DomainConfig[];
  organizationName?: string | null;
  domainNames?: string[];
  aiKind?: AiApplicationKind;
  browserConfig?: {
    httpInterceptionRules?: HttpInterceptionRuleWithRelations[];
    matchReplaceRules?: BrowserMatchReplaceRuleWithRelations[];
  } | null;
};

export type Application = PortalTarget;

export type Company = {
  id: string;
  name: string;
  tenantId: string | null;
};

export type TrackingConfig = {
  recordNetworkTraffic: boolean;
  recordPageScreenshots: boolean;
  responseMimeTypes?: string[];
  skipNoiseUrlExtensions?: boolean;
};

export type ProjectConfig = {
  companyId: string;
  targetId: string;
  targetType: TargetTypeType;
};

export type ProjectSyncState = {
  companyId: string;
  targetId: string;
  targetType: TargetTypeType;
  selectedCompany?: Company;
  selectedTarget?: PortalTarget;
};

type ProjectState = ProjectSyncState & {
  isTrackingEnabled: boolean;
  trackingConfig?: TrackingConfig;
  trackingCredentialId?: string;
  trackingTargetId?: string;
  trackingTargetType?: TargetTypeType;
  trackingCrawlerSessionId?: string;
  trackingBrowserIds: string[];
  registerTrackingBrowserId: (browserId: string) => void;
  unregisterTrackingBrowserId: (browserId: string) => void;
  reconcileTrackingBrowserIdsFromElectronSync: (args: {
    idsBeforeAwait: readonly string[];
    idsFromElectron: readonly string[];
  }) => void;
  updateProject: (config: Partial<ProjectConfig>) => void;
  setCompany: (company: Company) => void;
  setTarget: (target: PortalTarget) => void;
  updateSelectedTarget: (target: PortalTarget) => void;
  clearTargetSelection: () => void;
  clearProject: () => void;
  _clearCache: () => void;
  enableTracking: (
    config: TrackingConfig,
    credentialId?: string,
    crawlerSessionId?: string,
    targetId?: string,
    targetType?: TargetTypeType,
  ) => void;
  disableTracking: () => void;
};

const STORAGE_KEY = 'terra-proxy-project-store-v3';

function isChangingTarget({
  currentTargetId,
  currentTargetType,
  nextTargetId,
  nextTargetType,
}: {
  currentTargetId: string;
  currentTargetType: TargetTypeType;
  nextTargetId: string;
  nextTargetType: TargetTypeType;
}): boolean {
  return (
    currentTargetId !== nextTargetId || currentTargetType !== nextTargetType
  );
}

function resetProxyForTargetChange({
  previousTargetId,
  previousTargetType,
  nextTargetId,
  nextTargetType,
}: {
  previousTargetId: string;
  previousTargetType: TargetTypeType;
  nextTargetId: string | null;
  nextTargetType: TargetTypeType | null;
}): void {
  if (previousTargetId) {
    useLayoutStore.getState().closeTargetSpecificTabs({
      currentTargetId: previousTargetId,
      currentTargetType: previousTargetType,
      nextTargetId: nextTargetId ?? undefined,
      nextTargetType: nextTargetType ?? undefined,
    });
  }

  const proxyState = useProxyStore.getState();

  proxyState.setCurrentTarget({
    targetId: nextTargetId,
    targetType: nextTargetType,
  });
  proxyState.setActiveApplicationSettingsTab('general');
  proxyState.setActiveFilters({});
  proxyState.incrementHistoryVersion();
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      companyId: '',
      targetId: '',
      targetType: TARGET_TYPE.WEB_APPLICATION,
      selectedCompany: undefined,
      selectedTarget: undefined,
      isTrackingEnabled: false,
      trackingConfig: undefined,
      trackingCredentialId: undefined,
      trackingTargetId: undefined,
      trackingTargetType: undefined,
      trackingCrawlerSessionId: undefined,
      trackingBrowserIds: [],

      registerTrackingBrowserId: (browserId: string) => {
        const trimmed = browserId.trim();

        if (!trimmed) {
          return;
        }

        set((state) => {
          if (state.trackingBrowserIds.includes(trimmed)) {
            return state;
          }

          return {
            trackingBrowserIds: [...state.trackingBrowserIds, trimmed],
          };
        });
      },

      unregisterTrackingBrowserId: (browserId: string) => {
        set((state) => ({
          trackingBrowserIds: state.trackingBrowserIds.filter(
            (id) => id !== browserId,
          ),
        }));
      },

      reconcileTrackingBrowserIdsFromElectronSync: ({
        idsBeforeAwait,
        idsFromElectron,
      }) => {
        set((state) => ({
          trackingBrowserIds: mergeTrackingBrowserIdsAfterElectronSync({
            idsBeforeAwait,
            idsFromElectron,
            idsAfterAwait: state.trackingBrowserIds,
          }),
        }));
      },

      updateProject: (config: Partial<ProjectConfig>) => {
        const currentState = get();
        const nextTargetId =
          config.targetId !== undefined
            ? config.targetId
            : currentState.targetId;
        const nextTargetType =
          config.targetType !== undefined
            ? config.targetType
            : currentState.targetType;
        const isChangingTargetSelection = isChangingTarget({
          currentTargetId: currentState.targetId,
          currentTargetType: currentState.targetType,
          nextTargetId,
          nextTargetType,
        });
        const isChangingCompany =
          config.companyId !== undefined &&
          currentState.companyId !== config.companyId;

        if (
          currentState.isTrackingEnabled &&
          (isChangingTargetSelection || isChangingCompany)
        ) {
          void stopTrackingSession(
            currentState.trackingTargetId,
            currentState.trackingCrawlerSessionId,
          );
        }

        set({
          ...currentState,
          ...(config.companyId !== undefined && {
            companyId: config.companyId,
          }),
          ...(config.targetId !== undefined && {
            targetId: config.targetId,
          }),
          ...(config.targetType !== undefined && {
            targetType: config.targetType,
          }),
          ...((isChangingTargetSelection || isChangingCompany) &&
            currentState.isTrackingEnabled && {
              isTrackingEnabled: false,
              trackingConfig: undefined,
              trackingCredentialId: undefined,
              trackingTargetId: undefined,
              trackingTargetType: undefined,
              trackingCrawlerSessionId: undefined,
              trackingBrowserIds: [],
            }),
        });
      },

      setCompany: (company: Company) => {
        const currentState = get();
        const isChangingCompany = currentState.companyId !== company.id;

        if (!isChangingCompany) {
          const selectedCompany = currentState.selectedCompany;

          if (
            selectedCompany?.id === company.id &&
            selectedCompany?.tenantId === company.tenantId &&
            selectedCompany?.name === company.name
          ) {
            return;
          }

          set({
            companyId: company.id,
            selectedCompany: company,
          });

          return;
        }

        if (currentState.isTrackingEnabled) {
          void stopTrackingSession(
            currentState.trackingTargetId,
            currentState.trackingCrawlerSessionId,
          );
        }

        set({
          companyId: company.id,
          selectedCompany: company,
          targetId: '',
          targetType: TARGET_TYPE.WEB_APPLICATION,
          selectedTarget: undefined,
          ...(currentState.isTrackingEnabled && {
            isTrackingEnabled: false,
            trackingConfig: undefined,
            trackingCredentialId: undefined,
            trackingTargetId: undefined,
            trackingTargetType: undefined,
            trackingCrawlerSessionId: undefined,
            trackingBrowserIds: [],
          }),
        });

        if (currentState.targetId) {
          resetProxyForTargetChange({
            previousTargetId: currentState.targetId,
            previousTargetType: currentState.targetType,
            nextTargetId: null,
            nextTargetType: null,
          });
        }
      },

      setTarget: (target: PortalTarget) => {
        const currentState = get();
        const changingTarget = isChangingTarget({
          currentTargetId: currentState.targetId,
          currentTargetType: currentState.targetType,
          nextTargetId: target.id,
          nextTargetType: target.targetType,
        });

        if (changingTarget && currentState.isTrackingEnabled) {
          void stopTrackingSession(
            currentState.trackingTargetId,
            currentState.trackingCrawlerSessionId,
          );
        }

        set({
          targetId: target.id,
          targetType: target.targetType,
          selectedTarget: target,
          ...(changingTarget &&
            currentState.isTrackingEnabled && {
              isTrackingEnabled: false,
              trackingConfig: undefined,
              trackingCredentialId: undefined,
              trackingTargetId: undefined,
              trackingTargetType: undefined,
              trackingCrawlerSessionId: undefined,
              trackingBrowserIds: [],
            }),
        });

        if (changingTarget) {
          resetProxyForTargetChange({
            previousTargetId: currentState.targetId,
            previousTargetType: currentState.targetType,
            nextTargetId: target.id,
            nextTargetType: target.targetType,
          });
        }
      },

      updateSelectedTarget: (target: PortalTarget) => {
        const currentState = get();

        if (
          currentState.targetId === target.id &&
          currentState.targetType === target.targetType
        ) {
          set({
            selectedTarget: {
              ...target,
              domains:
                target.domains ?? currentState.selectedTarget?.domains ?? [],
              aiKind: target.aiKind ?? currentState.selectedTarget?.aiKind,
            },
          });
        }
      },

      clearTargetSelection: () => {
        const currentState = get();

        if (currentState.isTrackingEnabled) {
          void stopTrackingSession(
            currentState.trackingTargetId,
            currentState.trackingCrawlerSessionId,
          );
        }

        set({
          targetId: '',
          targetType: TARGET_TYPE.WEB_APPLICATION,
          selectedTarget: undefined,
          ...(currentState.isTrackingEnabled && {
            isTrackingEnabled: false,
            trackingConfig: undefined,
            trackingCredentialId: undefined,
            trackingTargetId: undefined,
            trackingTargetType: undefined,
            trackingCrawlerSessionId: undefined,
            trackingBrowserIds: [],
          }),
        });

        if (currentState.targetId) {
          resetProxyForTargetChange({
            previousTargetId: currentState.targetId,
            previousTargetType: currentState.targetType,
            nextTargetId: null,
            nextTargetType: null,
          });
        }
      },

      clearProject: () => {
        const currentState = get();

        if (currentState.isTrackingEnabled) {
          void stopTrackingSession(
            currentState.trackingTargetId,
            currentState.trackingCrawlerSessionId,
          );
        }

        if (currentState.targetId) {
          useLayoutStore.getState().closeTargetSpecificTabs({
            currentTargetId: currentState.targetId,
            currentTargetType: currentState.targetType,
          });
        }

        set({
          companyId: '',
          targetId: '',
          targetType: TARGET_TYPE.WEB_APPLICATION,
          selectedCompany: undefined,
          selectedTarget: undefined,
          isTrackingEnabled: false,
          trackingConfig: undefined,
          trackingCredentialId: undefined,
          trackingTargetId: undefined,
          trackingTargetType: undefined,
          trackingCrawlerSessionId: undefined,
          trackingBrowserIds: [],
        });

        const proxyState = useProxyStore.getState();

        proxyState.setCurrentTarget({ targetId: null, targetType: null });
        proxyState.setActiveFilters({});
        proxyState.incrementHistoryVersion();
      },

      enableTracking: (
        config: TrackingConfig,
        credentialId?: string,
        crawlerSessionId?: string,
        targetId?: string,
        targetType?: TargetTypeType,
      ) => {
        set({
          isTrackingEnabled: true,
          trackingConfig: config,
          trackingCredentialId: credentialId,
          trackingTargetId: targetId,
          trackingTargetType: targetType,
          trackingCrawlerSessionId: crawlerSessionId,
        });
      },

      disableTracking: () => {
        set({
          isTrackingEnabled: false,
          trackingConfig: undefined,
          trackingCredentialId: undefined,
          trackingTargetId: undefined,
          trackingTargetType: undefined,
          trackingCrawlerSessionId: undefined,
          trackingBrowserIds: [],
        });
      },

      _clearCache: () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('terra-proxy-project-store-v2');
        localStorage.removeItem('terra-proxy-project-store');
        window.location.reload();
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({
        companyId: state.companyId,
        targetId: state.targetId,
        targetType: state.targetType,
        selectedCompany: state.selectedCompany,
        selectedTarget: state.selectedTarget,
        isTrackingEnabled: state.isTrackingEnabled,
        trackingConfig: state.trackingConfig,
        trackingCredentialId: state.trackingCredentialId,
        trackingTargetId: state.trackingTargetId,
        trackingTargetType: state.trackingTargetType,
        trackingCrawlerSessionId: state.trackingCrawlerSessionId,
        trackingBrowserIds: state.trackingBrowserIds,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ProjectState> & {
          applicationId?: string;
          selectedApplication?: PortalTarget;
          trackingApplicationId?: string;
        };

        const migrated = migrateLegacyProjectPersistedState(persisted);

        return {
          ...currentState,
          ...persisted,
          ...migrated,
          trackingBrowserIds: Array.isArray(persisted.trackingBrowserIds)
            ? [...new Set(persisted.trackingBrowserIds.filter(Boolean))]
            : currentState.trackingBrowserIds,
          selectedTarget:
            (migrated.selectedTarget as PortalTarget | undefined) ??
            persisted.selectedTarget ??
            persisted.selectedApplication ??
            currentState.selectedTarget,
        };
      },
    },
  ),
);

export const useSelectedCompany = () =>
  useProjectStore((state) => state.selectedCompany);
export const useSelectedTarget = () =>
  useProjectStore((state) => state.selectedTarget);
export const useTenantId = () =>
  useProjectStore((state) => state.selectedCompany?.tenantId);
export const useCompanyId = () => useProjectStore((state) => state.companyId);
export const useTargetId = () => useProjectStore((state) => state.targetId);
export const useOptionalTargetId = (): string | undefined => {
  const targetId = useProjectStore((state) => state.targetId);

  return targetId || undefined;
};

export const useTargetType = () => useProjectStore((state) => state.targetType);
export const useTargetScope = () =>
  useProjectStore(
    useShallow((state) => ({
      targetId: state.targetId,
      targetType: state.targetType,
      hasTarget: Boolean(state.targetId),
    })),
  );
export const useAppTargetId = (): string | undefined =>
  useProjectStore((state) =>
    isAppTarget(state.targetType) ? state.targetId : undefined,
  );

export const useIsMcpServerTarget = (): boolean =>
  useProjectStore(
    (state) =>
      state.targetType === TARGET_TYPE.AI_APPLICATION &&
      state.selectedTarget?.aiKind === 'MCP_SERVER',
  );

export const useIsTrackingEnabled = () =>
  useProjectStore((state) => state.isTrackingEnabled);
export const useTrackingConfig = () =>
  useProjectStore((state) => state.trackingConfig);
export const useTrackingCredentialId = () =>
  useProjectStore((state) => state.trackingCredentialId);
